import { useState, useRef, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { LogOut, User as UserIcon, ChevronDown, KeyRound, Menu } from 'lucide-react'
import { useAuthStore } from '../stores/auth'
import { useUIStore } from '../stores/ui'
import { useCashflowStore } from '../stores/cashflow'
import { useBalanceCalc } from '../hooks/useBalanceCalc'
import PasswordChangeDialog from './PasswordChangeDialog'
import { ZaimuKun, type MascotMood, getZaimuKunMessage } from './mascot/zaimu-kun'

// パス → ページ名のマップ
const PAGE_TITLE_BY_PATH: Array<{ match: (p: string) => boolean; title: string }> = [
  { match: (p) => p.startsWith('/dashboard'), title: 'ダッシュボード' },
  { match: (p) => p.startsWith('/table'), title: '資金繰り表' },
  { match: (p) => p.startsWith('/input'), title: '収支登録' },
  { match: (p) => p.startsWith('/banks'), title: '銀行口座' },
  { match: (p) => p.startsWith('/masters') || p.startsWith('/vendors'), title: '取引先・カテゴリ' },
  { match: (p) => p.startsWith('/recurring'), title: '定期取引' },
  { match: (p) => p.startsWith('/reschedule') || p.startsWith('/debt'), title: 'リスケ管理' },
  { match: (p) => p.startsWith('/history'), title: '取引履歴' },
  { match: (p) => p.startsWith('/trash'), title: 'ゴミ箱' },
  { match: (p) => p.startsWith('/backup'), title: 'バックアップ' },
  { match: (p) => p.startsWith('/permissions'), title: '権限管理' },
  { match: (p) => p.startsWith('/ip-allowlist'), title: 'IP制限' },
  { match: (p) => p.startsWith('/login-history'), title: 'ログイン履歴' },
  { match: (p) => p.startsWith('/alerts'), title: 'アラート設定' },
  { match: (p) => p.startsWith('/audit-export'), title: '監査エクスポート' },
  { match: (p) => p.startsWith('/settings'), title: '設定' },
  { match: (p) => p.startsWith('/mfa'), title: '二要素認証' },
]

export default function TopBar() {
  const { user, logout } = useAuthStore()
  const pendingMutations = useUIStore((s) => s.pendingMutations)
  const toggleSidebar = useUIStore((s) => s.toggleSidebar)
  const cells = useCashflowStore((s) => s.cells)
  const categories = useCashflowStore((s) => s.categories)
  const cellMeta = useCashflowStore((s) => s.cellMeta)
  const settings = useCashflowStore((s) => s.settings)
  const banks = useCashflowStore((s) => s.banks)
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    if (menuOpen) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpen])

  const roleLabel = user?.role === 'admin' ? '管理者' : user?.role === 'manager' ? 'マネージャー' : '閲覧者'

  // 現在のパスからページタイトル決定
  const pageTitle = useMemo(() => {
    const found = PAGE_TITLE_BY_PATH.find((p) => p.match(location.pathname))
    return found?.title || '資金繰り管理'
  }, [location.pathname])

  // 今日から180日分の日付配列を生成（useBalanceCalc に渡す）
  const moodDates = useMemo(() => {
    const today = new Date()
    const result: Date[] = []
    for (let i = 0; i < 180; i++) {
      result.push(new Date(today.getFullYear(), today.getMonth(), today.getDate() + i))
    }
    return result
  }, [])

  // SpreadsheetPage と同じ useBalanceCalc を使って残高シミュレーション
  // → SpreadsheetPage の「資金ショート予測」と完全に一致する mood 判定が可能
  const moodBalances = useBalanceCalc(categories, cells, moodDates, settings, cellMeta, banks)

  // 設定画面でカスタマイズ可能な閾値（settings.settings に保存）
  const moodWorriedAt = Number(settings?.settings?.['mood_worried_threshold'] || '1000000')   // 1百万円未満で worried
  const moodHappyAt = Number(settings?.settings?.['mood_happy_threshold'] || '10000000')      // 1千万円超で happy

  const { mood, minBalance } = useMemo<{ mood: MascotMood; minBalance: number }>(() => {
    if (moodBalances.length === 0) return { mood: 'normal', minBalance: 0 }
    let minB = moodBalances[0].balance
    for (const b of moodBalances) {
      if (b.balance < minB) minB = b.balance
    }
    let m: MascotMood = 'normal'
    if (minB < 0) m = 'crying'
    else if (minB < moodWorriedAt) m = 'worried'
    else if (minB > moodHappyAt) m = 'happy'
    return { mood: m, minBalance: minB }
  }, [moodBalances, moodWorriedAt, moodHappyAt])

  const moodTitle = useMemo(() => {
    const message = getZaimuKunMessage(mood, { minBalance })
    return `今日から180日先までの最低予測残高: ${minBalance.toLocaleString()}円 — ${message}`
  }, [mood, minBalance])

  return (
    <header className="sticky top-0 z-30 h-14 flex items-center px-4 bg-white border-b border-slate-200 gap-2 shadow-sm">
      {/* モバイル用ハンバーガーボタン */}
      <button
        onClick={toggleSidebar}
        className="lg:hidden flex items-center justify-center w-9 h-9 rounded-lg hover:bg-slate-100 transition"
        aria-label="メニューを開く"
      >
        <Menu className="w-5 h-5 text-slate-700" />
      </button>

      {/* ザイムくん + ページタイトル（mood は今月の収支に連動） */}
      <div className="flex items-center gap-2 select-none" title={moodTitle}>
        <ZaimuKun mood={mood} size={32} />
        <span className="text-sm font-bold text-slate-800 truncate">{pageTitle}</span>
      </div>

      <div className="flex-1" />

      {/* 保存中インジケーター */}
      {pendingMutations > 0 && (
        <div className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
          <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
          保存中...
        </div>
      )}

      {/* ユーザーメニュー */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="flex items-center gap-2 px-3 h-9 rounded-lg hover:bg-slate-100 transition"
        >
          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
            <UserIcon className="w-4 h-4 text-blue-700" />
          </div>
          <div className="hidden sm:block text-left">
            <div className="text-xs font-medium text-slate-800 leading-tight">{user?.name}</div>
            <div className="text-[10px] text-slate-500 leading-tight">{roleLabel}</div>
          </div>
          <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
        </button>

        {menuOpen && (
          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1">
            <div className="px-3 py-2 border-b border-slate-100">
              <div className="text-sm font-medium text-slate-800 truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
            <button
              onClick={() => { setMenuOpen(false); setPasswordDialogOpen(true) }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition"
            >
              <KeyRound className="w-4 h-4" />パスワード変更
            </button>
            <button
              onClick={() => { setMenuOpen(false); logout() }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition"
            >
              <LogOut className="w-4 h-4" />ログアウト
            </button>
          </div>
        )}
      </div>

      {/* パスワード変更ダイアログ */}
      {passwordDialogOpen && (
        <PasswordChangeDialog onClose={() => setPasswordDialogOpen(false)} />
      )}
    </header>
  )
}
